import React from 'react';
import { Box, CircularProgress, DialogContent, Divider, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { BhiDialog } from 'src/UIKit';
import { CopyToClipboardButton } from 'src/components/CopyToClipboardButton';
import { getTimePassed } from 'src/constants/helpers';
import { Message } from 'src/constants/types';
import { MessageTypeChips } from '../MessageTypeChips';

// The subset of campaign fields the analytics view needs. `CampaignListItem`
// satisfies this structurally, and it can also be passed via router state when
// opening a read-only campaign in the builder. The delivery breakdown and
// scheduled messages are optional because the campaign-builder analytics
// endpoint only returns summary metrics.
export interface CampaignAnalyticsData {
  id: string;
  name: string;
  numberOfContacts?: number | null;
  totalReplies?: number | null;
  numberOfRespondedContacts?: number | null;
  lastMessageSentAt?: string | null;
  lastResponseAt?: string | null;
  scheduledMessages?: Message[];
  messagesDelivered?: number | null;
  messagesPending?: number | null;
  messagesFailed?: number | null;
}

interface CampaignAnalyticsModalProps {
  open: boolean;
  onClose: () => void;
  campaign: CampaignAnalyticsData | null;
  loading?: boolean;
}

const formatNumber = (value: number) => value.toLocaleString();

const formatPercent = (value: number, total: number) => {
  if (total <= 0) return '0%';

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
  }).format((value / total) * 100) + '%';
};

const formatActivityDate = (value: string) => dayjs(value).format('MMM D, YYYY');

export const CampaignAnalyticsModal: React.FC<CampaignAnalyticsModalProps> = ({
  open,
  onClose,
  campaign,
  loading = false,
}) => {
  if (!campaign && !loading) return null;

  const messagesDelivered = campaign?.messagesDelivered || 0;
  const messagesPending = campaign?.messagesPending || 0;
  const messagesFailed = campaign?.messagesFailed || 0;
  const totalMessages = messagesDelivered + messagesPending + messagesFailed;
  const deliveryPercent = formatPercent(messagesDelivered, totalMessages);
  const pendingPercent = formatPercent(messagesPending, totalMessages);
  const failedPercent = formatPercent(messagesFailed, totalMessages);

  const messageTypes = campaign?.scheduledMessages?.map(m => m.type) ?? [];

  const metrics = campaign
    ? [
        {
          label: 'Contacts',
          value: formatNumber(campaign.numberOfContacts || 0),
          detail: 'in campaign',
        },
        {
          label: 'Messages sent',
          value: formatNumber(totalMessages),
          detail: `${formatNumber(messagesDelivered)} delivered`,
        },
        {
          label: 'Failed',
          value: formatNumber(messagesFailed),
          detail: `${failedPercent} of sent`,
        },
        {
          label: 'Replies',
          value: formatNumber(campaign.totalReplies || 0),
          detail: `${formatNumber(campaign.numberOfRespondedContacts || 0)} contacts`,
        },
      ]
    : [];

  const activityRows = campaign
    ? [
        {
          label: 'Last message sent',
          timeAgo: campaign.lastMessageSentAt ? `${getTimePassed(campaign.lastMessageSentAt)} ago` : 'N/A',
          date: campaign.lastMessageSentAt ? formatActivityDate(campaign.lastMessageSentAt) : null,
        },
        {
          label: 'Last reply',
          timeAgo: campaign.lastResponseAt ? `${getTimePassed(campaign.lastResponseAt)} ago` : 'N/A',
          date: campaign.lastResponseAt ? formatActivityDate(campaign.lastResponseAt) : null,
        },
      ]
    : [];

  return (
    <BhiDialog
      isOpen={open}
      onClose={onClose}
      title={
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontSize={11}
              fontWeight="fontWeightBold"
              letterSpacing={0}
              lineHeight={1}
            >
              Campaign analytics
            </Typography>
            {messageTypes.length > 0 && <MessageTypeChips messageTypes={messageTypes} />}
          </Box>

          {campaign && (
            <>
              <Typography
                variant="h5"
                component="div"
                color="text.primary"
                fontSize={{ xs: 20, sm: 23 }}
                fontWeight="fontWeightBold"
                lineHeight={1.2}
                mb={0.75}
                pr={1}
                sx={{ wordBreak: 'break-word' }}
              >
                {campaign.name}
              </Typography>

              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  maxWidth: '100%',
                  '& .MuiIconButton-root': {
                    p: 0.25,
                  },
                }}
              >
                <CopyToClipboardButton text={campaign.id} tooltip="Copy campaign ID">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      maxWidth: { xs: 220, sm: 360 },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {campaign.id}
                  </Typography>
                </CopyToClipboardButton>
              </Box>
            </>
          )}
        </Box>
      }
      maxWidth={false}
      paperSx={{ width: 640, maxWidth: '94vw' }}
    >
      <Divider />
      <DialogContent sx={{ px: 2.25, py: 2.5 }}>
        {loading || !campaign ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 355 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 1.25 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight="fontWeightBold"
                  letterSpacing={0}
                  lineHeight={1}
                >
                  Delivery
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="fontWeightSemiBold">
                  <Box component="span" color="text.primary">
                    {deliveryPercent} delivered
                  </Box>
                  {messagesFailed > 0 && (
                    <>
                      {' . '}
                      <Box component="span">{formatNumber(messagesFailed)} failed</Box>
                    </>
                  )}
                </Typography>
              </Box>

              <Box
                borderRadius={999}
                bgcolor="action.hover"
                sx={{ display: 'flex', width: '100%', height: 8, overflow: 'hidden' }}
              >
                <Box width={deliveryPercent} bgcolor="success.main" />
                <Box width={pendingPercent} bgcolor="warning.light" />
                <Box width={failedPercent} bgcolor="error.main" />
              </Box>
            </Box>

            <Box
              border={1}
              borderColor="divider"
              borderRadius={2}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(4, minmax(140px, 1fr))', sm: 'repeat(4, 1fr)' },
                overflowX: 'auto',
                mb: 2.5,
              }}
            >
              {metrics.map((metric, index) => (
                <Box
                  key={metric.label}
                  borderLeft={index === 0 ? 'none' : 1}
                  borderColor="divider"
                  sx={{ minWidth: 0, px: 1.5, py: 1.75 }}
                >
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    display="block"
                    fontSize={11}
                    fontWeight="fontWeightBold"
                    letterSpacing={0}
                    lineHeight={1.4}
                    whiteSpace="nowrap"
                  >
                    {metric.label}
                  </Typography>
                  <Typography variant="h5" fontWeight="fontWeightBold" lineHeight={1.25} mt={0.5}>
                    {metric.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {metric.detail}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box borderTop={1} borderColor="divider">
              {activityRows.map(row => (
                <Box
                  key={row.label}
                  borderBottom={1}
                  borderColor="divider"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr auto auto' },
                    gap: { xs: 0.5, sm: 1 },
                    alignItems: 'baseline',
                    py: 1.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography variant="body2" fontWeight="fontWeightBold">
                    {row.timeAgo}
                  </Typography>
                  {row.date && (
                    <Typography variant="caption" color="text.secondary" fontWeight="fontWeightSemiBold">
                      {row.date}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
    </BhiDialog>
  );
};
